/*****************************************************************************
 * Copyright(c) 2025 Qorus Inc
 * All rights reserved
 *****************************************************************************
 * NAME: src/utils/authStorage.ts
 * DEVELOPER: Jaime Rivera
 * DESCRIPTION: Token and authentication data persistence using localStorage
 * REVISIONS:
 * Date Change      ID              Author              Description
 * -----------      -----------     --------------      ------------------------------------
 * 20-Nov-2025      HOTFIX-PKCE    Jaime Rivera        Initial implementation
 *****************************************************************************/

 export interface StoredAuthData {
    accessToken: string;
    tokenExpiry: number;
    tokenType: string;
    sessionId: string;
    codeVerifier: string;
    embedId: string;
  }
  
  export interface StoredPKCEData {
    codeVerifier: string;
    codeChallenge: string;
    state: string;
    embedId: string;
  }
  
  class AuthStorage {
    private static readonly AUTH_KEY = 'widget_forge_auth';
    private static readonly PKCE_KEY = 'widget_forge_pkce';
    private static readonly SESSION_KEY = 'widget_forge_session';
  
    setAuthData(authData: StoredAuthData): void {
      try {
        localStorage.setItem(AuthStorage.AUTH_KEY, JSON.stringify(authData));
      } catch (error) {
      }
    }
  
    getAuthData(): StoredAuthData | null {
      try {
        const stored = localStorage.getItem(AuthStorage.AUTH_KEY);
        
        if (!stored) {
          return null;
        }
        
        const authData = JSON.parse(stored) as StoredAuthData;
        
        const now = Date.now();
        const isExpired = now >= authData.tokenExpiry;
        
        if (isExpired) {
          this.clearAuthData();
          return null;
        }
        
        return authData;
      } catch (error) {
        this.clearAuthData();
        return null;
      }
    }
  
    clearAuthData(): void {
      try {
        localStorage.removeItem(AuthStorage.AUTH_KEY);
      } catch (error) {
      }
    }
  
    setPKCEData(pkceData: StoredPKCEData): void {
      try {
        localStorage.setItem(AuthStorage.PKCE_KEY, JSON.stringify(pkceData));
      } catch (error) {
      }
    }
  
    getPKCEData(): StoredPKCEData | null {
      try {
        const stored = localStorage.getItem(AuthStorage.PKCE_KEY);
        if (!stored) return null;
        
        return JSON.parse(stored) as StoredPKCEData;
      } catch (error) {
        this.clearPKCEData();
        return null;
      }
    }
  
    clearPKCEData(): void {
      try {
        localStorage.removeItem(AuthStorage.PKCE_KEY);
      } catch (error) {
      }
    }
  
    setSessionId(sessionId: string): void {
      try {
        localStorage.setItem(AuthStorage.SESSION_KEY, sessionId);
      } catch (error) {
      }
    }
  
    getSessionId(): string | null {
      try {
        return localStorage.getItem(AuthStorage.SESSION_KEY);
      } catch (error) {
        return null;
      }
    }
  
    clearSessionId(): void {
      try {
        localStorage.removeItem(AuthStorage.SESSION_KEY);
      } catch (error) {
      }
    }
  
    clearAll(): void {
      this.clearAuthData();
      this.clearPKCEData();
      this.clearSessionId();
    }
  
    hasValidAuthData(): boolean {
      const authData = this.getAuthData();
      return authData !== null && Date.now() < authData.tokenExpiry;
    }
  
    hasPKCEData(): boolean {
      const pkceData = this.getPKCEData();
      return pkceData !== null && !!pkceData.codeVerifier && !!pkceData.codeChallenge;
    }
  }
  
  export const authStorage = new AuthStorage();
  export default authStorage;